import { useState, useEffect, useCallback } from 'react';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import VerLinksButton from './VerLinksButton';

const PAGE_SIZE = 20;
const ACCEPT = 'image/*,video/*,application/pdf';

function MediaThumb({ m }) {
  if (m.type === 'image') return <img src={m.url} alt="" style={{ width: '54px', height: '54px', objectFit: 'cover', borderRadius: '6px', border: `1px solid ${theme.colors.border}` }} />;
  if (m.type === 'video') return <div style={{ width: '54px', height: '54px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.colors.background, fontSize: '1.4rem' }}>🎬</div>;
  return <div style={{ width: '54px', height: '54px', borderRadius: '6px', border: `1px solid ${theme.colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', background: theme.colors.background, fontSize: '1.4rem' }}>📄</div>;
}

// Renderiza un medio a tamaño completo dentro del modal de detalle.
function MediaFull({ m }) {
  const box = { width: '100%', borderRadius: '8px', border: `1px solid ${theme.colors.border}`, background: theme.colors.background };
  if (m.type === 'image') return <img src={m.url} alt="" style={{ ...box, maxHeight: '420px', objectFit: 'contain' }} />;
  if (m.type === 'video') return <video src={m.url} controls style={{ ...box, maxHeight: '420px' }} />;
  return (
    <a href={m.url} target="_blank" rel="noopener noreferrer" style={{ ...box, display: 'flex', alignItems: 'center', gap: '8px', padding: '14px 16px', color: theme.colors.text, textDecoration: 'none', boxSizing: 'border-box' }}>
      <span style={{ fontSize: '1.4rem' }}>📄</span> <span>Abrir documento PDF</span>
    </a>
  );
}

function Noticias({ token }) {
  const isMobile = useIsMobile();
  const authH = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [existingMedia, setExistingMedia] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [viewing, setViewing] = useState(null);

  const openCreate = () => {
    setEditingId(null); setTitle(''); setDescription(''); setFiles([]); setExistingMedia([]);
    setCreateError(''); setShowCreate(true);
  };
  const openEdit = (n) => {
    setEditingId(n.id); setTitle(n.title); setDescription(n.description || '');
    setFiles([]); setExistingMedia(n.media || []); setCreateError(''); setShowCreate(true);
  };
  const removeExistingMedia = (idx) => setExistingMedia(prev => prev.filter((_, i) => i !== idx));

  const fetchData = useCallback(() => {
    setLoading(true);
    fetch(`/api/news?page=${page}`, { headers: authH })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitSave = async () => {
    if (!title.trim()) { setCreateError('El título es obligatorio'); return; }
    setSubmitting(true);
    setCreateError('');
    const fd = new FormData();
    fd.append('title', title);
    fd.append('description', description);
    files.forEach(f => fd.append('media', f));

    let r;
    if (editingId) {
      fd.append('keep_media', JSON.stringify(existingMedia.map(m => m.url)));
      r = await fetch(`/api/news/${editingId}`, { method: 'PUT', headers: authH, body: fd });
    } else {
      r = await fetch('/api/news', { method: 'POST', headers: authH, body: fd });
    }

    if (r.ok) {
      setShowCreate(false);
      setEditingId(null); setTitle(''); setDescription(''); setFiles([]); setExistingMedia([]);
      if (editingId) fetchData();
      else if (page === 1) fetchData(); else setPage(1);
    } else {
      const json = await r.json().catch(() => ({}));
      setCreateError(json.message || 'Error al guardar la noticia');
    }
    setSubmitting(false);
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta noticia?')) return;
    setDeletingId(id);
    const r = await fetch(`/api/news/${id}`, { method: 'DELETE', headers: authH });
    if (r.ok) setData(prev => prev.filter(n => n.id !== id));
    setDeletingId(null);
  };

  const inputStyle = { width: '100%', padding: '10px 12px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', color: theme.colors.text, fontSize: '0.92rem', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
  const labelStyle = { display: 'block', color: theme.colors.textMuted, fontSize: '0.78rem', marginBottom: '5px', fontWeight: 500 };

  return (
    <div style={{ padding: isMobile ? '1.2rem 0.8rem' : '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.2rem' }}>
        <h2 style={{ color: theme.colors.primary, margin: 0 }}>Noticias</h2>
        <button onClick={openCreate} style={{ background: theme.colors.primary, color: '#fff', border: 'none', borderRadius: '8px', padding: '9px 18px', cursor: 'pointer', fontWeight: 600 }}>＋ Crear noticia</button>
      </div>

      <div style={{ marginBottom: '1.2rem' }}>
        <VerLinksButton />
      </div>

      {loading ? (
        <p style={{ color: theme.colors.textMuted }}>Cargando...</p>
      ) : data.length === 0 ? (
        <p style={{ color: theme.colors.textMuted }}>No has creado noticias.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                  {['#', 'Título', 'Descripción', 'Medios', 'Fecha', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: theme.colors.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map(n => (
                  <tr key={n.id} style={{ borderBottom: `1px solid ${theme.colors.border}22` }}>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, fontFamily: 'monospace', fontSize: '0.78rem' }}>{n.id}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.text, fontWeight: 500, maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.description || '—'}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {(n.media || []).slice(0, 4).map((m, i) => <MediaThumb key={i} m={m} />)}
                        {(n.media || []).length > 4 && <span style={{ color: theme.colors.textMuted, alignSelf: 'center', fontSize: '0.78rem' }}>+{n.media.length - 4}</span>}
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', color: theme.colors.textMuted, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                      {n.created_at ? new Date(n.created_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => setViewing(n)} style={{ background: 'none', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary, padding: '3px 12px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem' }}>
                          Ver
                        </button>
                        <button onClick={() => openEdit(n)} style={{ background: 'none', border: `1px solid ${theme.colors.border}`, color: theme.colors.text, padding: '3px 12px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem' }}>
                          Editar
                        </button>
                        <button onClick={() => remove(n.id)} disabled={deletingId === n.id} style={{ background: 'none', border: `1px solid ${theme.colors.error}`, color: theme.colors.error, padding: '3px 12px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem' }}>
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '1rem', alignItems: 'center' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>‹</button>
              <span style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Página {page} de {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={{ padding: '5px 12px', borderRadius: '5px', border: `1px solid ${theme.colors.border}`, background: 'none', color: theme.colors.text, cursor: 'pointer' }}>›</button>
            </div>
          )}
        </>
      )}

      {/* Modal ver noticia (detalle + medios + comentarios + likes) */}
      {viewing && (
        <div onClick={() => setViewing(null)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0.8rem' : '1.5rem' }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '12px', border: `1px solid ${theme.colors.border}`, width: '100%', maxWidth: '680px', maxHeight: '92vh', overflow: 'auto', padding: isMobile ? '1.2rem' : '1.8rem', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>{viewing.title}</h3>
              <button onClick={() => setViewing(null)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', margin: '0 0 1rem' }}>
              {viewing.created_at ? new Date(viewing.created_at).toLocaleString('es-CO', { dateStyle: 'long', timeStyle: 'short' }) : ''}
            </p>

            {viewing.description && (
              <p style={{ color: theme.colors.text, fontSize: '0.92rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', margin: '0 0 1.2rem' }}>{viewing.description}</p>
            )}

            {(viewing.media || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '1.2rem' }}>
                {viewing.media.map((m, i) => <MediaFull key={i} m={m} />)}
              </div>
            )}

            {/* Reacciones */}
            <div style={{ display: 'flex', gap: '18px', alignItems: 'center', padding: '12px 0', borderTop: `1px solid ${theme.colors.border}`, borderBottom: `1px solid ${theme.colors.border}`, marginBottom: '1.2rem' }}>
              <span style={{ color: theme.colors.text, fontSize: '0.95rem' }}>👍 {viewing.likes || 0} me gusta</span>
              <span style={{ color: theme.colors.text, fontSize: '0.95rem' }}>👎 {viewing.dislikes || 0} no me gusta</span>
            </div>

            {/* Comentarios */}
            <h4 style={{ color: theme.colors.text, margin: '0 0 0.8rem', fontSize: '0.95rem' }}>
              Comentarios ({(viewing.comments || []).length})
            </h4>
            {(viewing.comments || []).length === 0 ? (
              <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem' }}>Aún no hay comentarios.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {viewing.comments.map((c, i) => (
                  <div key={i} style={{ background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
                      <strong style={{ color: theme.colors.text, fontSize: '0.85rem' }}>{c.name || 'Anónimo'}</strong>
                      {c.created_at && <span style={{ color: theme.colors.textMuted, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>{new Date(c.created_at).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</span>}
                    </div>
                    <p style={{ color: theme.colors.textMuted, fontSize: '0.85rem', margin: 0, whiteSpace: 'pre-wrap' }}>{c.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal crear noticia */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '0.8rem' : '1.5rem' }}>
          <div style={{ backgroundColor: theme.colors.surface, borderRadius: '12px', border: `1px solid ${theme.colors.border}`, width: '100%', maxWidth: '600px', maxHeight: '92vh', overflow: 'auto', padding: isMobile ? '1.2rem' : '1.8rem', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
              <h3 style={{ color: theme.colors.primary, margin: 0 }}>{editingId ? 'Editar noticia' : 'Nueva noticia'}</h3>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>✕</button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Título *</label>
              <input value={title} onChange={e => setTitle(e.target.value)} maxLength={250} style={inputStyle} placeholder="Título de la noticia" />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Descripción</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)} rows={5} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Contenido de la noticia" />
            </div>

            {editingId && existingMedia.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={labelStyle}>Medios actuales (toca ✕ para eliminar)</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {existingMedia.map((m, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <MediaThumb m={m} />
                      <button onClick={() => removeExistingMedia(i)} title="Eliminar medio" style={{ position: 'absolute', top: '-7px', right: '-7px', width: '20px', height: '20px', borderRadius: '50%', border: 'none', background: theme.colors.error, color: '#fff', cursor: 'pointer', fontSize: '0.7rem', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>{editingId ? 'Agregar nuevos medios' : 'Medios (imágenes, videos o PDF)'}</label>
              <input type="file" accept={ACCEPT} multiple onChange={e => setFiles(Array.from(e.target.files || []))} style={{ ...inputStyle, cursor: 'pointer' }} />
              {files.length > 0 && (
                <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', marginTop: '0.4rem' }}>{files.length} archivo(s) nuevo(s) seleccionado(s)</p>
              )}
            </div>
            {createError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '0.8rem' }}>{createError}</p>}
            <button onClick={submitSave} disabled={submitting} style={{ background: theme.colors.primary, color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 20px', cursor: 'pointer', fontWeight: 600, width: '100%' }}>
              {submitting ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Publicar noticia')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Noticias;
